import { useEffect, useState } from "react";
import { fetchAbandonedMeetings } from "@/api/vercel.ts";

// Define types
interface AbandonedMeeting {
  id: string;
  fullMeetingName: string;
  trackName: string;
  State: string;
  reason: string;
  meetingDate: string;
  date: string;
  lastUpdated: {
    _seconds: number;
    _nanoseconds: number;
  };
}

interface ApiResponse {
  status: string;
  data: {
    status: string;
    data: AbandonedMeeting[];
  };
  message?: string;
}

const getWorkspace = () => {
  // Use environment variable if available
  if (import.meta.env.VITE_APP_WORKSPACE) {
    return import.meta.env.VITE_APP_WORKSPACE;
  }

  // Fallback to hostname-based detection
  const hostname = window.location.hostname;

  if (hostname === "staging.lexi.tips") {
    return "staging";
  } else if (hostname === "lexi.tips") {
    return "prod";
  }
  return "dev";
};

// Use the correct Vite environment variable syntax
const workspace = getWorkspace();
let API_URL: string;
if (workspace === "dev" || workspace === "staging" || workspace === "prod") {
  API_URL = `https://lexi-${workspace}-front-end-mcdr62tc5a-ts.a.run.app`;
} else {
  API_URL = `https://australia-southeast1-staible-returns.cloudfunctions.net/staible-returns-${workspace}-front-end`;
}

export const useAbandonedMeetings = (date: string) => {
  const [abandonedMeetings, setAbandonedMeetings] = useState<
    AbandonedMeeting[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Use the fetchAbandonedMeetings function from the Vercel API client
        const apiResponse: ApiResponse = await fetchAbandonedMeetings(date);

        if (apiResponse.status === "success" && apiResponse.data?.data) {
          setAbandonedMeetings(apiResponse.data.data);
        } else {
          setAbandonedMeetings([]);
          setError(new Error("Invalid API response format"));
        }
      } catch (err) {
        console.error("Error fetching abandoned meetings:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setAbandonedMeetings([]);
      } finally {
        setLoading(false);
      }
    };

    if (date) {
      fetchData();
    }
  }, [date]);

  // Helper function to check if a track is abandoned
  const isTrackAbandoned = (trackName: string): boolean => {
    return abandonedMeetings.some(
      (meeting) => meeting.trackName.toLowerCase() === trackName.toLowerCase(),
    );
  };

  // Helper function to get reason for abandonment
  const getAbandonmentReason = (trackName: string): string | null => {
    const meeting = abandonedMeetings.find(
      (meeting) => meeting.trackName.toLowerCase() === trackName.toLowerCase(),
    );
    return meeting ? meeting.reason : null;
  };

  return {
    abandonedMeetings,
    loading,
    error,
    isTrackAbandoned,
    getAbandonmentReason,
  };
};
